import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import { getErrorScripts } from "./assistant-service";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import CodeEditor from "../common/code-editor";

const ErrorScriptView = () => {
  const { apiCall } = useApiCall();
  const { errorToast, successToast } = useToast();

  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(
    new Date().toISOString().split("T")[0], // Today's date in YYYY-MM-DD format
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentScript, setCurrentScript] = useState(null);

  useEffect(() => {
    if (selectedDay) {
      fetchErrorScripts();
    }
  }, [selectedDay]);

  const fetchErrorScripts = async () => {
    try {
      setLoading(true);
      // Use YYYY-MM-DD format for API
      const apiDay = selectedDay;
      const data = await getErrorScripts(apiCall, apiDay);

      if (data && data.scripts) {
        // Add id for DataGrid and process scripts
        const processedScripts = data.scripts.map((script, index) => ({
          id: script.scriptId || index,
          scriptId: script.scriptId,
          script: script.script,
          day: script.day,
        }));
        setScripts(processedScripts);
      } else {
        setScripts([]);
      }
      setLoading(false);
    } catch (error) {
      errorToast("Failed to fetch error scripts");
      setLoading(false);
    }
  };

  const handleRowClick = params => {
    setCurrentScript(params.row);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentScript(null);
  };

  const handleDayChange = event => {
    setSelectedDay(event.target.value);
  };

  const columns = [
    {
      field: "scriptId",
      headerName: "Script ID",
      width: 200,
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 180,
            display: "block",
          }}
          title={params.value}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "day",
      headerName: "Day",
      width: 120,
    },
    {
      field: "script",
      headerName: "Script Preview",
      flex: 1,
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
            display: "block",
          }}
          title={params.value}
        >
          {params.value?.substring(0, 100)}...
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: params => (
        <Button
          variant="contained"
          size="small"
          onClick={e => {
            e.stopPropagation();
            handleRowClick(params);
          }}
        >
          View Script
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <TextField
              label="Select Day"
              type="date"
              value={selectedDay}
              onChange={handleDayChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ width: 200 }}
            />
            <Button variant="contained" onClick={fetchErrorScripts}>
              Refresh
            </Button>
          </Box>
        </Grid>

        <Grid item>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <DataGrid
              rows={scripts}
              columns={columns}
              onRowClick={handleRowClick}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 20 },
                },
              }}
              pageSizeOptions={[10, 20, 50]}
              autoHeight
              disableColumnSelector
              sx={{
                border: "none",
                [`& .${gridClasses.cell}`]: {
                  border: "none",
                  "&:focus": {
                    outline: "none",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
                [`& .${gridClasses.columnHeader}`]: {
                  border: "none",
                  "&:focus": {
                    outline: "none",
                  },
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                  cursor: "pointer",
                },
                "& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
              }}
            />
          )}
        </Grid>
      </Grid>

      {/* Script Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Error Script - {currentScript?.scriptId}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Day: {currentScript?.day}
            </Typography>
            <CodeEditor
              value={currentScript?.script || ""}
              editable={false}
              height="600px"
              language="javascript"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErrorScriptView;
